import { useState, useEffect, useCallback } from "react";
import { notification } from 'antd';
import { useNavigate } from "react-router-dom";
import { 
  User, Building2, Phone, Mail, FileText, Edit2, Save, 
  Briefcase, Calendar, MapPin, Camera, Award
} from "lucide-react";
import api from "../../API/api";
import useFormDraft from "../../hooks/useFormDraft";

export default function ProfilePage() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [user, setUser] = useState({
        name: "",
        company: "",
        phone: "",
        email: "",
        bio: "",
        course: "",
        graduationYear: "",
        location: "",
    });

    const [editing, setEditing] = useState(false);
    const [activeTab, setActiveTab] = useState("info");
    const [loading, setLoading] = useState(true);

    const isProfileDraftEmpty = useCallback((u) => {
        if (!u) return true;
        return !u.company && !u.phone && !u.email && !u.bio && !u.location;
    }, []);

    const {
        markEdited,
        clearDraft,
        promptLeave,
        draftModals,
        draftBanner,
    } = useFormDraft({
        namespace: "perfil",
        data: user,
        setData: setUser,
        enabled: editing,
        ready: !loading,
        isEmpty: isProfileDraftEmpty,
    });

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('me'));
        if (userData) {
            setMe(userData);
            setUser({
                name: (userData?.first_name || "") + " " + (userData?.last_name || ""),
                company: userData?.company || "",
                phone: userData?.contact_number || "",
                email: userData?.personal_email || "",
                bio: userData?.about || "",
                course: userData?.course_name || "Não informado",
                graduationYear: userData?.graduation_year || "Não informado",
                location: userData?.location || "Não informado",
            });
        }
        setLoading(false);
    }, []);

    const handleChange = (e) => {
        markEdited();
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        try {
            await api.put('v1/users/my/', {
                company: user.company,
                contact_number: user.phone,
                personal_email: user.email,
                about: user.bio,
                location: user.location,
            });
            
            const updatedUser = {
                ...me,
                company: user.company,
                contact_number: user.phone,
                personal_email: user.email,
                about: user.bio,
                location: user.location,
            };
            
            localStorage.setItem('me', JSON.stringify(updatedUser));
            setMe(updatedUser);
            
            notification.success({
                message: "Sucesso",
                description: "Seu perfil foi actualizado com sucesso!",
                placement: "topRight",
            });

            await clearDraft();
            setEditing(false);
        } catch (error) {
            notification.error({
                message: "Erro",
                description: "Ocorreu um erro ao atualizar seu perfil.",
                placement: "topRight",
            });
        }
    };

    const resetProfileForm = () => {
        setUser({
            name: (me?.first_name || "") + " " + (me?.last_name || ""),
            company: me?.company || "",
            phone: me?.contact_number || "",
            email: me?.personal_email || "",
            bio: me?.about || "",
            course: me?.course_name || "Não informado",
            graduationYear: me?.graduation_year || "Não informado",
            location: me?.location || "Não informado",
        });
        setEditing(false);
    };

    const handleCancel = () => {
        promptLeave(resetProfileForm);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                    <p className="mt-4 text-gray-500">Carregando perfil...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {draftModals}
            {editing && draftBanner}
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                    {/* Sidebar - Profile Card */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Avatar Section */}
                            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 pt-8 pb-16 text-center">
                                <div className="relative inline-block">
                                    <div className="w-28 h-28 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white shadow-lg flex items-center justify-center">
                                        <span className="text-5xl font-bold text-white">
                                            {user.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    {editing && (
                                        <button className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md hover:bg-gray-50 transition">
                                            <Camera className="w-4 h-4 text-emerald-600" />
                                        </button>
                                    )}
                                </div>
                                <h2 className="text-xl font-bold text-white mt-4">{user.name}</h2>
                                <p className="text-emerald-100 text-sm mt-1">{user.course}</p>
                            </div>

                            {/* Info Section */}
                            <div className="p-6 space-y-4">
                                <div className="flex items-center gap-3 text-gray-600">
                                    <Calendar className="w-5 h-5 text-emerald-500" />
                                    <div>
                                        <p className="text-xs text-gray-400">Ano de Formatura</p>
                                        <p className="text-sm font-medium">{user.graduationYear}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-gray-600">
                                    <MapPin className="w-5 h-5 text-emerald-500" />
                                    <div>
                                        <p className="text-xs text-gray-400">Localização</p>
                                        <p className="text-sm font-medium">{user.location}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-gray-600">
                                    <Briefcase className="w-5 h-5 text-emerald-500" />
                                    <div>
                                        <p className="text-xs text-gray-400">Empresa</p>
                                        <p className="text-sm font-medium">{user.company || "Não informado"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Tabs */}
                            <div className="border-b border-gray-100 px-6">
                                <div className="flex gap-6">
                                    <button
                                        onClick={() => setActiveTab("info")}
                                        className={`py-4 text-sm font-medium transition-colors relative ${
                                            activeTab === "info" ? "text-emerald-600" : "text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                        Informações Pessoais
                                        {activeTab === "info" && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("professional")}
                                        className={`py-4 text-sm font-medium transition-colors relative ${
                                            activeTab === "professional" ? "text-emerald-600" : "text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                        Dados Profissionais
                                        {activeTab === "professional" && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="p-6">
                                {/* Tab: Informações Pessoais */}
                                {activeTab === "info" && (
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Mail className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={user.email}
                                                    onChange={handleChange}
                                                    disabled={!editing}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-all ${
                                                        editing ? "border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white" : "border-gray-200 bg-gray-50 text-gray-500"
                                                    }`}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Phone className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Telefone
                                                </label>
                                                <input
                                                    type="text"
                                                    name="phone"
                                                    value={user.phone}
                                                    onChange={handleChange}
                                                    disabled={!editing}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-all ${
                                                        editing ? "border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white" : "border-gray-200 bg-gray-50 text-gray-500"
                                                    }`}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <MapPin className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                Localização
                                            </label>
                                            <input
                                                type="text"
                                                name="location"
                                                value={user.location}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-all ${
                                                    editing ? "border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white" : "border-gray-200 bg-gray-50 text-gray-500"
                                                }`}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <FileText className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                Sobre Mim
                                            </label>
                                            <textarea
                                                name="bio"
                                                value={user.bio}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                rows={4}
                                                className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-all resize-none ${
                                                    editing ? "border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white" : "border-gray-200 bg-gray-50 text-gray-500"
                                                }`}
                                                placeholder="Conte um pouco sobre sua trajetória profissional e acadêmica..."
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Tab: Dados Profissionais */}
                                {activeTab === "professional" && (
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Building2 className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Empresa / Organização
                                                </label>
                                                <input
                                                    type="text"
                                                    name="company"
                                                    value={user.company}
                                                    onChange={handleChange}
                                                    disabled={!editing}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-all ${
                                                        editing ? "border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white" : "border-gray-200 bg-gray-50 text-gray-500"
                                                    }`}
                                                    placeholder="Nome da empresa onde trabalha"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Award className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Curso
                                                </label>
                                                <input
                                                    type="text"
                                                    value={user.course}
                                                    disabled
                                                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Calendar className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Ano de Formatura
                                                </label>
                                                <input
                                                    type="text"
                                                    value={user.graduationYear}
                                                    disabled
                                                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <Briefcase className="inline w-4 h-4 mr-1 text-emerald-500" />
                                                    Cargo / Função
                                                </label>
                                                <input
                                                    type="text"
                                                    value="A definir"
                                                    disabled
                                                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-6 mt-4 border-t border-gray-100">
                                    {!editing ? (
                                        <button
                                            onClick={() => setEditing(true)}
                                            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                            Editar Perfil
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={handleCancel}
                                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg text-sm transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                onClick={handleSave}
                                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Save className="w-4 h-4" />
                                                Salvar Alterações
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </div>
    );
}